import { ClassroomTabEnum, PracticeTestTypeEnum, QuestionLocationTypeEnum } from '__generatedTypes__/globalTypes'
import { PlatformSlug } from 'constants/consts'
import { SubjectExtended } from 'hooks/usePlatformsAndSubjects'
import { FullLengthTestsTabs, FULL_LENGTH_TESTS_TAB_PARAM } from 'pagesNew/FullLengthTests/consts'
import { isReadingSubject } from 'pagesNew/QuestionBanks/utils'
import { isGREPassagesAvailable } from 'pagesNew/Questions/Form/questionTypeConfig'
import { cmsRoutes } from 'routes/cmsRoutes'

export const PT_MIN_SCORES: Record<string, number> = {
  [PlatformSlug.DATBooster]: 200,
  [PlatformSlug.DATCrusher]: 13,
  [PlatformSlug.OATBooster]: 240,
  [PlatformSlug.PATBooster]: 13,
  [PlatformSlug.CARSBooster]: 118,
  [PlatformSlug.MCATBooster]: 118, // TODO: update to correct one
  [PlatformSlug.GREBooster]: 118, // TODO: update to correct one
  [PlatformSlug.NBEOPartOne]: 118, // TODO: update to correct one
}

export const READING_TYPE_PARAM = 'type'
export const READING_QB = 'readingQB'
export const READING_PT = 'readingPT'

const getPTDefaultTitle = (type: PracticeTestTypeEnum): string => {
  switch (type) {
    case PracticeTestTypeEnum.ReadingPracticeTest:
      return 'Reading Practice Test'
    case PracticeTestTypeEnum.ReadingQuestionBank:
      return 'Reading Question Bank'
    case PracticeTestTypeEnum.MiniTest:
      return 'Mini Test'
    case PracticeTestTypeEnum.ReadingMiniTest:
      return 'Reading Mini Test'
  }

  return 'Practice Test'
}

export type PTConfig = {
  type: PracticeTestTypeEnum
  titleSingle: string
  titleMultiple: string
  dashboardLink: string
  dashboardSearchParams?: Record<string, string>
  createLink: string
  createSearchParams?: Record<string, string>
  editLink: string
  classroomTab: ClassroomTabEnum | null
  questionLocationType: QuestionLocationTypeEnum
  alwaysDisplayLiveSwitch?: boolean
}

export const getPTConfig = (
  typeProp: PracticeTestTypeEnum,
  platformSlug: PlatformSlug,
  subject: Pick<SubjectExtended, 'id' | 'slug'> | null,
  isQB = false
): PTConfig | null => {
  const isReading = isReadingSubject(subject?.slug ?? '')
  const isGrePracticeTest = isGREPassagesAvailable(platformSlug)

  // we reuse PT entity for Reading QB only
  if (isQB && ((!!subject && !isReading) || typeProp !== PracticeTestTypeEnum.PracticeTest)) {
    return null
  }

  if (typeProp === PracticeTestTypeEnum.PracticeTest) {
    const type = isGrePracticeTest
      ? PracticeTestTypeEnum.MultiSectionGREPracticeTest
      : isReading
      ? isQB
        ? PracticeTestTypeEnum.ReadingQuestionBank
        : PracticeTestTypeEnum.ReadingPracticeTest
      : PracticeTestTypeEnum.PracticeTest

    const titleSingle = getPTDefaultTitle(type)

    return {
      type,
      titleSingle,
      titleMultiple: `${titleSingle}s`,
      dashboardLink: isQB ? cmsRoutes.QUESTION_BANK_LIST : cmsRoutes.PRACTICE_TEST_LIST,
      createLink: cmsRoutes.PRACTICE_TEST_CREATE,
      createSearchParams: isReading ? { [READING_TYPE_PARAM]: isQB ? READING_QB : READING_PT } : undefined,
      editLink: cmsRoutes.PRACTICE_TEST_EDIT,
      classroomTab: isReading ? ClassroomTabEnum.ReadingPracticeTests : ClassroomTabEnum.PracticeTests,
      questionLocationType: QuestionLocationTypeEnum.PT,
      alwaysDisplayLiveSwitch: isGrePracticeTest,
    }
  }

  if (typeProp === PracticeTestTypeEnum.MiniTest) {
    const type = isReading ? PracticeTestTypeEnum.ReadingMiniTest : PracticeTestTypeEnum.MiniTest
    const titleSingle = getPTDefaultTitle(type)

    return {
      type,
      titleSingle,
      titleMultiple: `${titleSingle}s`,
      dashboardLink: cmsRoutes.FULL_LENGTH_TESTS, // we show mini tests in the separate tab of full length tests page
      dashboardSearchParams: {
        [FULL_LENGTH_TESTS_TAB_PARAM]: FullLengthTestsTabs.MINI_LENGTH,
      },
      createLink: cmsRoutes.MINI_TEST_CREATE,
      editLink: cmsRoutes.MINI_TEST_EDIT,
      classroomTab: null, // we don't have a separate tab for mini tests
      questionLocationType: QuestionLocationTypeEnum.MiniTest,
    }
  }

  return null
}
